import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useLanguage, API_URL } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import {
  CreditCard, Clock, CheckCircle, XCircle, Download,
  Smartphone, AlertCircle, Receipt, Wallet, PieChart,
  ArrowUpRight, Plus, Settings, RefreshCw, Eye, Phone,
  Building2, FileText, ChevronRight
} from 'lucide-react';

const METHODS = [
  { value: 'm_pesa', label: 'M-Pesa', color: '#4CAF50' },
  { value: 'orange_money', label: 'Orange Money', color: '#FF6600' },
  { value: 'airtel_money', label: 'Airtel Money', color: '#ED1C24' },
  { value: 'africell', label: 'Africell', color: '#0066CC' },
  { value: 'illico_cash', label: 'Illico Cash', color: '#1B5E20' },
  { value: 'bank_transfer', label: 'Virement bancaire', color: '#37474F' }
];

const FEE_TYPE_LABELS = {
  academic: 'Academique',
  exam: 'Examen',
  lab: 'Laboratoire',
  library: 'Bibliotheque',
  inscription: 'Inscription',
  other: 'Autre'
};

const STATUS_CONFIG = {
  completed: { label: 'Paye', icon: CheckCircle, className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  pending: { label: 'En attente', icon: Clock, className: 'bg-amber-50 text-amber-700 border-amber-200' },
  failed: { label: 'Echoue', icon: XCircle, className: 'bg-red-50 text-red-700 border-red-200' }
};

export const PaymentsPage = () => {
  const { user } = useAuth();
  const isStudent = user?.role === 'student';
  const isAdmin = ['super_admin', 'co_admin', 'rector', 'sg_admin', 'budget_director', 'admin_staff'].includes(user?.role);

  return isStudent ? <StudentPaymentsView /> : isAdmin ? <AdminPaymentsView /> : <NoAccessView />;
};

// ============ STUDENT VIEW ============
const StudentPaymentsView = () => {
  const [feeSummary, setFeeSummary] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [selectedFee, setSelectedFee] = useState(null);
  const [receiptPayment, setReceiptPayment] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [feesRes, paymentsRes] = await Promise.all([
        axios.get(`${API_URL}/payments/my-fees`),
        axios.get(`${API_URL}/payments`)
      ]);
      setFeeSummary(feesRes.data);
      setPayments(paymentsRes.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des donnees');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <LoadingSkeleton />;

  const progressPercent = feeSummary ? Math.round((feeSummary.total_paid / Math.max(feeSummary.total_due, 1)) * 100) : 0;

  return (
    <div className="space-y-6 animate-slide-in" data-testid="payments-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Paiements</h1>
          <p className="text-sm text-gray-500 mt-1">Gerez vos frais academiques et suivez vos paiements</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-payments">
          <RefreshCw className="w-4 h-4 mr-2" />Actualiser
        </Button>
      </div>

      {/* Summary Cards */}
      {feeSummary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Wallet} label="Total du" value={`$${feeSummary.total_due.toLocaleString()}`} color="blue" testId="stat-total-due" />
          <StatCard icon={CheckCircle} label="Paye" value={`$${feeSummary.total_paid.toLocaleString()}`} color="green" testId="stat-total-paid" />
          <StatCard icon={Clock} label="En attente" value={`$${feeSummary.total_pending.toLocaleString()}`} color="amber" testId="stat-total-pending" />
          <StatCard icon={AlertCircle} label="Restant" value={`$${feeSummary.remaining.toLocaleString()}`} color="red" testId="stat-remaining" />
        </div>
      )}

      {/* Progress Bar */}
      {feeSummary && feeSummary.total_due > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Progression des paiements</span>
              <span className="text-sm font-bold text-[var(--tenant-primary)]">{progressPercent}%</span>
            </div>
            <Progress value={progressPercent} className="h-3" data-testid="payment-progress" />
            <p className="text-xs text-gray-500 mt-2">
              ${feeSummary.total_paid.toLocaleString()} sur ${feeSummary.total_due.toLocaleString()} USD {feeSummary.academic_year && `- ${feeSummary.academic_year}`}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Pending Alert */}
      {feeSummary && feeSummary.remaining > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5 sm:mt-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900">
                Solde restant: ${feeSummary.remaining.toLocaleString()} USD
              </p>
              <p className="text-xs text-amber-700">
                Effectuez vos paiements avant les dates limites pour eviter les penalites.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fee Breakdown */}
      <Tabs defaultValue="fees" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="fees" data-testid="tab-fees">Mes frais</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Historique</TabsTrigger>
        </TabsList>

        <TabsContent value="fees" className="space-y-3 mt-4">
          {feeSummary?.fees?.map(fee => (
            <FeeCard
              key={fee.id}
              fee={fee}
              onPay={() => { setSelectedFee(fee); setPayDialogOpen(true); }}
            />
          ))}
          {(!feeSummary?.fees || feeSummary.fees.length === 0) && (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">
                Aucun frais configure pour votre promotion.
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <PaymentHistoryTable payments={payments} onViewReceipt={setReceiptPayment} />
        </TabsContent>
      </Tabs>

      {/* Payment Methods */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Methodes de paiement acceptees</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {METHODS.map(m => (
              <div key={m.value} className="flex flex-col items-center gap-2 p-3 rounded-md border hover:border-[var(--tenant-primary)] transition-colors cursor-default">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: m.color + '20' }}>
                  <Smartphone className="w-4 h-4" style={{ color: m.color }} />
                </div>
                <span className="text-xs text-center font-medium">{m.label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Pay Dialog */}
      {selectedFee && (
        <PaymentDialog
          open={payDialogOpen}
          onOpenChange={setPayDialogOpen}
          fee={selectedFee}
          onSuccess={fetchData}
        />
      )}

      {/* Receipt Dialog */}
      {receiptPayment && (
        <ReceiptDialog
          payment={receiptPayment}
          onClose={() => setReceiptPayment(null)}
        />
      )}
    </div>
  );
};

// ============ FEE CARD ============
const FeeCard = ({ fee, onPay }) => {
  const paidPercent = Math.round((fee.paid / Math.max(fee.total_amount, 1)) * 100);

  return (
    <Card data-testid={`fee-card-${fee.id}`}>
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-semibold">{fee.name}</span>
              <Badge variant="outline" className="text-xs">
                {FEE_TYPE_LABELS[fee.fee_type] || fee.fee_type}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span>Total: ${fee.total_amount.toLocaleString()}</span>
              <span>Paye: ${fee.paid.toLocaleString()}</span>
              {fee.due_date && <span>Echeance: {fee.due_date}</span>}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Progress value={paidPercent} className="h-1.5 flex-1" />
              <span className="text-xs font-medium">{paidPercent}%</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {fee.is_fully_paid ? (
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                <CheckCircle className="w-3 h-3 mr-1" />Solde
              </Badge>
            ) : (
              <Button size="sm" onClick={onPay} data-testid={`pay-fee-${fee.id}`}>
                <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                Payer ${fee.remaining.toLocaleString()}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============ PAYMENT DIALOG ============
const PaymentDialog = ({ open, onOpenChange, fee, onSuccess }) => {
  const [method, setMethod] = useState('m_pesa');
  const [phone, setPhone] = useState('');
  const [amount, setAmount] = useState(fee.remaining.toString());
  const [step, setStep] = useState('form'); // form, confirming, result
  const [paymentResult, setPaymentResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone || !amount) return;

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/payments/initiate`, {
        fee_config_id: fee.id,
        method,
        phone_number: phone,
        amount: parseFloat(amount),
        installment_number: fee.payments_count + 1
      });
      setPaymentResult(res.data);
      setStep('confirming');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'initiation du paiement');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (success) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/payments/${paymentResult.payment_id}/simulate-confirm`, success);
      setPaymentResult(prev => ({ ...prev, ...res.data }));
      setStep('result');
      if (success) {
        toast.success('Paiement confirme avec succes!');
      }
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const resetDialog = () => {
    setStep('form');
    setPaymentResult(null);
    setLoading(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={resetDialog}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg">
            {step === 'form' && 'Effectuer un paiement'}
            {step === 'confirming' && 'Confirmation Mobile Money'}
            {step === 'result' && (paymentResult?.status === 'completed' ? 'Paiement reussi' : 'Paiement echoue')}
          </DialogTitle>
        </DialogHeader>

        {step === 'form' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="p-3 bg-gray-50 rounded-md border">
              <p className="text-sm font-medium">{fee.name}</p>
              <p className="text-xs text-gray-500">Restant: ${fee.remaining.toLocaleString()} {fee.currency}</p>
            </div>

            <div>
              <Label>Montant (USD)</Label>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                max={fee.remaining}
                min={1}
                step="0.01"
                required
                data-testid="payment-amount-input"
              />
              {fee.installments_allowed > 1 && (
                <p className="text-xs text-gray-500 mt-1">
                  Paiement en {fee.installments_allowed} tranches autorise
                </p>
              )}
            </div>

            <div>
              <Label>Methode de paiement</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {METHODS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Numero de telephone</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 bg-gray-100 px-3 py-2 rounded-md border">+243</span>
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="8XX XXX XXX"
                  required
                  data-testid="payment-phone-input"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={resetDialog}>Annuler</Button>
              <Button type="submit" disabled={loading} data-testid="payment-submit-btn">
                {loading ? 'Traitement...' : `Payer $${parseFloat(amount || 0).toLocaleString()}`}
              </Button>
            </div>
          </form>
        )}

        {step === 'confirming' && (
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-md text-center">
              <Phone className="w-10 h-10 mx-auto text-amber-600 mb-2 animate-pulse" />
              <p className="text-sm font-medium text-amber-900">
                En attente de confirmation...
              </p>
              <p className="text-xs text-amber-700 mt-1">
                {paymentResult?.instructions}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Transaction: {paymentResult?.transaction_id}
              </p>
            </div>

            <p className="text-xs text-center text-gray-500">
              (Mode simule) Confirmez ou rejetez manuellement :
            </p>

            <div className="flex gap-2 justify-center">
              <Button
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50"
                onClick={() => handleConfirm(false)}
                disabled={loading}
                data-testid="payment-reject-btn"
              >
                <XCircle className="w-4 h-4 mr-1" />Rejeter
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => handleConfirm(true)}
                disabled={loading}
                data-testid="payment-confirm-btn"
              >
                <CheckCircle className="w-4 h-4 mr-1" />Confirmer le paiement
              </Button>
            </div>
          </div>
        )}

        {step === 'result' && (
          <div className="space-y-4 text-center">
            {paymentResult?.status === 'completed' ? (
              <>
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <div>
                  <p className="font-semibold text-emerald-900">Paiement confirme!</p>
                  <p className="text-sm text-gray-500 mt-1">Transaction: {paymentResult?.transaction_id}</p>
                </div>
              </>
            ) : (
              <>
                <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
                <div>
                  <p className="font-semibold text-red-900">Paiement echoue</p>
                  <p className="text-sm text-gray-500 mt-1">Veuillez reessayer ou contacter votre operateur.</p>
                </div>
              </>
            )}
            <Button onClick={resetDialog} className="w-full" data-testid="payment-done-btn">Fermer</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============ PAYMENT HISTORY TABLE ============
const PaymentHistoryTable = ({ payments, onViewReceipt }) => {
  if (!payments?.length) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-gray-500">
          <Receipt className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          Aucun paiement effectue pour le moment.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                <TableHead>Methode</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead className="text-center">Statut</TableHead>
                <TableHead>Date</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map(payment => (
                <TableRow key={payment.id} data-testid={`payment-row-${payment.id}`}>
                  <TableCell>
                    <div>
                      <p className="text-sm font-medium">{payment.fee_name || payment.description}</p>
                      {payment.installment_number && (
                        <p className="text-xs text-gray-500">
                          Tranche {payment.installment_number}/{payment.total_installments}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <MethodBadge method={payment.method} />
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold">
                    ${payment.amount?.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-center">
                    <StatusBadge status={payment.status} />
                  </TableCell>
                  <TableCell className="text-sm text-gray-500">
                    {new Date(payment.created_at).toLocaleDateString('fr-FR')}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {payment.status === 'completed' && (
                        <Button variant="ghost" size="icon" onClick={() => onViewReceipt(payment)} data-testid={`receipt-btn-${payment.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

// ============ RECEIPT DIALOG ============
const ReceiptDialog = ({ payment, onClose }) => {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const res = await axios.get(`${API_URL}/payments/${payment.id}`);
        setDetail(res.data);
      } catch {
        setDetail(payment);
      }
    };
    fetchDetail();
  }, [payment]);

  const p = detail || payment;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Recu de paiement</DialogTitle>
        </DialogHeader>
        <div className="space-y-4" data-testid="payment-receipt">
          <div className="border rounded-md p-4 space-y-3">
            <div className="flex justify-between items-center border-b pb-2">
              <span className="text-xs text-gray-500">JoSmart Uni</span>
              <StatusBadge status={p.status} />
            </div>
            {p.university_name && (
              <div className="text-center py-2">
                <Building2 className="w-6 h-6 mx-auto text-[var(--tenant-primary)] mb-1" />
                <p className="text-sm font-bold">{p.university_name}</p>
              </div>
            )}
            <ReceiptRow label="Description" value={p.fee_name || p.description} />
            <ReceiptRow label="Montant" value={`$${p.amount?.toLocaleString()} ${p.currency || 'USD'}`} bold />
            <ReceiptRow label="Methode" value={METHODS.find(m => m.value === p.method)?.label || p.method} />
            <ReceiptRow label="Transaction" value={p.transaction_id || '-'} />
            {p.phone_number && <ReceiptRow label="Telephone" value={`+243 ${p.phone_number}`} />}
            {p.student_name && <ReceiptRow label="Etudiant" value={p.student_name} />}
            {p.matricule && <ReceiptRow label="Matricule" value={p.matricule} />}
            <ReceiptRow label="Date" value={p.paid_at ? new Date(p.paid_at).toLocaleString('fr-FR') : new Date(p.created_at).toLocaleString('fr-FR')} />
          </div>
          <Button onClick={onClose} className="w-full" variant="outline">Fermer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ ADMIN PAYMENTS VIEW ============
const AdminPaymentsView = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [feeConfigs, setFeeConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [receiptPayment, setReceiptPayment] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, paymentsRes, configsRes] = await Promise.all([
        axios.get(`${API_URL}/payments/admin/stats`),
        axios.get(`${API_URL}/payments`),
        axios.get(`${API_URL}/payments/fees/config`)
      ]);
      setStats(statsRes.data);
      setPayments(paymentsRes.data);
      setFeeConfigs(configsRes.data);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <LoadingSkeleton />;

  const filteredPayments = statusFilter === 'all'
    ? payments
    : payments.filter(p => p.status === statusFilter);

  return (
    <div className="space-y-6 animate-slide-in" data-testid="payments-admin-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Gestion des paiements</h1>
          <p className="text-sm text-gray-500 mt-1">Tableau de bord financier</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />Actualiser
          </Button>
          <Button size="sm" onClick={() => setConfigDialogOpen(true)} data-testid="config-fees-btn">
            <Settings className="w-4 h-4 mr-2" />Configurer frais
          </Button>
        </div>
      </div>

      {/* Admin Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={CheckCircle} label="Total collecte" value={`$${stats.total_collected?.toLocaleString()}`} color="green" testId="admin-stat-collected" />
          <StatCard icon={Clock} label="En attente" value={`$${stats.total_pending_amount?.toLocaleString()}`} color="amber" testId="admin-stat-pending" />
          <StatCard icon={Receipt} label="Transactions" value={stats.total_payments} color="blue" testId="admin-stat-transactions" />
          <StatCard icon={XCircle} label="Echoues" value={stats.failed_count} color="red" testId="admin-stat-failed" />
        </div>
      )}

      {/* Method Breakdown */}
      {stats?.method_breakdown && Object.keys(stats.method_breakdown).length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <PieChart className="w-4 h-4" />Repartition par methode
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Object.entries(stats.method_breakdown).map(([method, data]) => {
                const methodInfo = METHODS.find(m => m.value === method);
                return (
                  <div key={method} className="p-3 border rounded-md">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: methodInfo?.color || '#666' }} />
                      <span className="text-xs font-medium">{methodInfo?.label || method}</span>
                    </div>
                    <p className="text-lg font-bold">${data.total?.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">{data.count} transaction(s)</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payments Table with Filter */}
      <Tabs defaultValue="all" onValueChange={setStatusFilter}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="all">Tous ({payments.length})</TabsTrigger>
            <TabsTrigger value="completed">Payes ({payments.filter(p => p.status === 'completed').length})</TabsTrigger>
            <TabsTrigger value="pending">En attente ({payments.filter(p => p.status === 'pending').length})</TabsTrigger>
            <TabsTrigger value="failed">Echoues ({payments.filter(p => p.status === 'failed').length})</TabsTrigger>
          </TabsList>
        </div>
      </Tabs>

      <AdminPaymentTable payments={filteredPayments} onViewReceipt={setReceiptPayment} />

      {/* Fee Config List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4" />Configurations des frais ({feeConfigs.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                  <TableHead>Annee</TableHead>
                  <TableHead>Tranches</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {feeConfigs.map(config => (
                  <TableRow key={config.id}>
                    <TableCell className="font-medium text-sm">{config.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{FEE_TYPE_LABELS[config.fee_type] || config.fee_type}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold">${config.amount?.toLocaleString()}</TableCell>
                    <TableCell className="text-sm">{config.academic_year}</TableCell>
                    <TableCell className="text-sm text-center">{config.installments_allowed}</TableCell>
                    <TableCell>
                      {config.is_active
                        ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">Actif</Badge>
                        : <Badge className="bg-gray-50 text-gray-500 text-xs">Inactif</Badge>
                      }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Config Dialog */}
      <FeeConfigDialog
        open={configDialogOpen}
        onOpenChange={setConfigDialogOpen}
        onSuccess={fetchData}
      />

      {receiptPayment && <ReceiptDialog payment={receiptPayment} onClose={() => setReceiptPayment(null)} />}
    </div>
  );
};

// ============ ADMIN PAYMENT TABLE ============
const AdminPaymentTable = ({ payments, onViewReceipt }) => {
  if (!payments?.length) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-gray-500">
          Aucun paiement pour ce filtre.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Etudiant</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Methode</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead className="text-center">Statut</TableHead>
                <TableHead>Date</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map(p => (
                <TableRow key={p.id} data-testid={`admin-payment-row-${p.id}`}>
                  <TableCell>
                    <div>
                      <p className="text-sm font-medium">{p.student_name || '-'}</p>
                      <p className="text-xs text-gray-500">{p.matricule || ''}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm">{p.fee_name || p.description}</p>
                  </TableCell>
                  <TableCell><MethodBadge method={p.method} /></TableCell>
                  <TableCell className="text-right font-mono font-semibold">${p.amount?.toLocaleString()}</TableCell>
                  <TableCell className="text-center"><StatusBadge status={p.status} /></TableCell>
                  <TableCell className="text-sm text-gray-500">
                    {new Date(p.created_at).toLocaleDateString('fr-FR')}
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={() => onViewReceipt(p)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

// ============ FEE CONFIG DIALOG ============
const FeeConfigDialog = ({ open, onOpenChange, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '', amount: '', fee_type: 'academic', academic_year: '2025-2026',
    installments_allowed: '1', due_date: '', description: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/payments/fees/config`, {
        ...formData,
        amount: parseFloat(formData.amount),
        installments_allowed: parseInt(formData.installments_allowed)
      });
      toast.success('Configuration de frais creee');
      onOpenChange(false);
      setFormData({ name: '', amount: '', fee_type: 'academic', academic_year: '2025-2026', installments_allowed: '1', due_date: '', description: '' });
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const update = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Nouvelle configuration de frais</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Nom du frais</Label>
              <Input value={formData.name} onChange={e => update('name', e.target.value)}
                placeholder="Ex: Frais academiques" required data-testid="fee-config-name" />
            </div>
            <div>
              <Label>Montant (USD)</Label>
              <Input type="number" value={formData.amount} onChange={e => update('amount', e.target.value)}
                placeholder="1500" min="1" step="0.01" required data-testid="fee-config-amount" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={formData.fee_type} onValueChange={v => update('fee_type', v)}>
                <SelectTrigger data-testid="fee-config-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(FEE_TYPE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Annee academique</Label>
              <Input value={formData.academic_year} onChange={e => update('academic_year', e.target.value)}
                placeholder="2025-2026" required />
            </div>
            <div>
              <Label>Nombre de tranches</Label>
              <Input type="number" value={formData.installments_allowed}
                onChange={e => update('installments_allowed', e.target.value)}
                min="1" max="12" required />
            </div>
            <div>
              <Label>Date limite</Label>
              <Input type="date" value={formData.due_date} onChange={e => update('due_date', e.target.value)} />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={formData.description} onChange={e => update('description', e.target.value)}
                placeholder="Description optionnelle" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={loading} data-testid="fee-config-submit">
              {loading ? 'Creation...' : 'Creer la configuration'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============ SHARED COMPONENTS ============
const StatCard = ({ icon: Icon, label, value, color, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-${color}-100`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  return (
    <Badge variant="outline" className={`gap-1 text-xs ${config.className}`}>
      <Icon className="w-3 h-3" />{config.label}
    </Badge>
  );
};

const MethodBadge = ({ method }) => {
  const info = METHODS.find(m => m.value === method);
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: info?.color || '#666' }} />
      <span className="text-xs">{info?.label || method}</span>
    </div>
  );
};

const ReceiptRow = ({ label, value, bold }) => (
  <div className="flex justify-between text-sm">
    <span className="text-gray-500">{label}</span>
    <span className={bold ? 'font-bold text-[var(--tenant-primary)]' : 'font-medium'}>{value}</span>
  </div>
);

const LoadingSkeleton = () => (
  <div className="space-y-4 animate-pulse" data-testid="payments-loading">
    <div className="h-8 bg-gray-200 rounded w-48" />
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-gray-200 rounded-md" />)}
    </div>
    <div className="h-64 bg-gray-200 rounded-md" />
  </div>
);

const NoAccessView = () => (
  <div className="text-center py-12" data-testid="no-access">
    <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
    <p className="text-gray-500">Acces non autorise a la gestion des paiements.</p>
  </div>
);

export default PaymentsPage;
